import React, { useMemo } from 'react';
import { PlayIcon, PauseIcon, RewindIcon, FastForwardIcon } from './icons/PlaybackIcons';

interface PlaybackControlsProps {
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  playbackSpeed: number;
  onPlayPause: () => void;
  onSeek: (time: number) => void;
  onSpeedChange: (speed: number) => void;
}

const formatTime = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const PlaybackControls: React.FC<PlaybackControlsProps> = ({
  currentTime,
  duration,
  isPlaying,
  playbackSpeed,
  onPlayPause,
  onSeek,
  onSpeedChange,
}) => {
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSeek(Number(e.target.value));
  };
  
  const formattedCurrentTime = useMemo(() => formatTime(currentTime), [currentTime]);
  const formattedDuration = useMemo(() => formatTime(duration), [duration]);

  return (
    <div className="flex items-center gap-4 w-full">
       <button onClick={onPlayPause} className="p-2 bg-cyan-500 text-gray-900 rounded-full hover:bg-cyan-400 transition-colors shadow-lg flex-shrink-0">
        {isPlaying ? <PauseIcon className="w-6 h-6" /> : <PlayIcon className="w-6 h-6" />}
      </button>
      
      <span className="text-sm text-gray-400 w-16 text-right">{formattedCurrentTime}</span>
      
      <input
        type="range"
        min="0"
        max={duration}
        value={currentTime}
        onChange={handleSeek}
        className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-cyan-500 flex-grow"
      />

      <span className="text-sm text-gray-400 w-16 text-left">{formattedDuration}</span>
      
      <div className="flex items-center gap-1">
        <button onClick={() => onSeek(currentTime - 10000)} className="p-2 rounded-full hover:bg-gray-700 transition-colors" title="Rewind 10s">
          <RewindIcon className="w-5 h-5" />
        </button>
        <button onClick={() => onSeek(currentTime + 10000)} className="p-2 rounded-full hover:bg-gray-700 transition-colors" title="Forward 10s">
          <FastForwardIcon className="w-5 h-5" />
        </button>
        <div className="relative">
          <select 
            value={playbackSpeed} 
            onChange={(e) => onSpeedChange(Number(e.target.value))}
            className="bg-gray-700 text-white rounded-md px-2 py-1 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
            title="Playback speed"
          >
            <option value={0.5}>0.5x</option>
            <option value={1}>1x</option>
            <option value={2}>2x</option>
            <option value={4}>4x</option>
            <option value={8}>8x</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default PlaybackControls;